import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { type ColDef, GridApi, type TextFilterModel, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { IEventMessage, IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import { helperService } from "src/services/helper.service";
import { useDebounce } from "src/hooks/useDebounce";

import { Modal, useModal } from "src/components/Modal/Modal";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import SendMessage, { SendMessageType } from "src/components/SendMessage/SendMessage";
import { download } from "src/components/Download/DownloadManager";
import { toast } from "src/components/Toast/ToastManager";
import { ColumnSelectionModal } from "src/components/ColumnSelectionModal/ColumnSelectionModal";
import EntryFormsFilterModal from "src/components/EntryFormsFilterModal/EntryFormsFilterModal";
import WeighInsModal from "src/components/WeighInsModal/WeighInsModal";
import { registrationColumns } from "src/constants/registrations-columns";

import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./RegistrantsTab.module.scss";

interface IProps {
  event: IEventSummary,
  currentTab: number,
  tabIndex: number
}

interface IRow {
  callName: string,
  armband: string,
  registrationNumber: string,
  breed: string,
  ownerName: string,
  ownerEmail: string,
  handlerName: string,
  handlerEmail: string,
  show: string,
  showDate: string,
  registeredAt: string,
  actions: any,
  isJuniorDup?: any,
}

interface IFilters {
  ownerName?: TextFilterModel
  ownerEmail?: TextFilterModel
}

const RegistrantsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);

  const [gridApi, setGridApi] = useState<GridApi<IRow>>();

  const [ownerNameSearch, setOwnerNameSearch] = useState<string>("");
  const debouncedOwnerNameSearch = useDebounce<string>(ownerNameSearch, 800);

  const [emailSearch, setEmailSearch] = useState<string>("");
  const debouncedEmailSearch = useDebounce<string>(emailSearch, 800);

  const { isShown, toggle } = useModal();
  const [isShowingEmailConfirmation, setIsShowingEmailConfirmation] = useState<boolean>(false);
  
  const { isShown: isColumnModalShown, toggle: toggleColumnModal } = useModal();
  const { isShown: isEntryFormsFilterShown, toggle: toggleEntryFormsFilter } = useModal();
  const { isShown: isWeighInsShown, toggle: toggleWeighIns } = useModal();

  const handleRegistrationsColumnSelection = (selectedColumns: string[]) => {
    download.pushDownload({
      type: "armband",
      eventId: props.event._id,
      selectedColumns: selectedColumns
    });
    toggleColumnModal();
  };

  let menuActions:IContextMenuOption[] = [
    {
      label: "Export Registrants",
      onClick: () => {
        toggleColumnModal();
      }
    },
    {
      label: "Export Registrations",
      onClick: () => {
        download.pushDownload({
          type: "scores",
          eventId: props.event._id
        });
      }
    },
    {
      label: "Email Registrants",
      onClick: () => {
        toggle();
      }
    }
  ];

  if (props.event.sanctioning_club !== Constants.sanctioning_club.PWDCA && 
      !(props.event.sanctioning_club === Constants.sanctioning_club.GENERAL &&
        !props.event.show_types?.some(st => st.show_type === Constants.show_type.dock_diving))) {
    /* // @ts-expect-error: toSpliced exists on []*/
    menuActions = menuActions.toSpliced(2, 0, {
      label: "Download Forms",
      onClick: () => {
        toggleEntryFormsFilter();
      }
    })
  }

  // UKC Weight Pull is weighed in once per event, not once per trial — see
  // docs/ukc-weight-pull-weigh-in-redesign.md.
  if (props.event.show_types?.some(st => st.show_type === Constants.show_type.weight_pull)) {
    menuActions = [
      ...menuActions,
      {
        label: "Enter Weigh Ins",
        onClick: () => {
          toggleWeighIns();
        }
      },
      {
        label: "Download Weigh-In Forms",
        onClick: () => {
          download.pushDownload({
            type: "event_weigh_in_forms",
            eventId: props.event._id
          });
        }
      }
    ];
  }

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  }

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setOwnerNameSearch("");
      setEmailSearch("");
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  const addRegistrantRow = (row: any, rows:IRow[], isJuniorDup = false) => {
    // Build full registered name with prefix and suffix
    let fullRegisteredName = row.dog.registered_name || '';
    if (row.dog.prefix_title) {
      fullRegisteredName = `${row.dog.prefix_title} ${fullRegisteredName}`;
    }
    if (row.dog.suffix_title) {
      fullRegisteredName = `${fullRegisteredName} ${row.dog.suffix_title}`;
    }

    // Only show handler name if using_agent or using_non_agent is checked
    const handlerName = (row.using_agent || row.using_non_agent) ? (row.agent_name || "") : "";

    rows.push({
      isJuniorDup,
      callName: row.dog.call_name + " (" + fullRegisteredName + ")",
      armband: isJuniorDup ? row.arm_number_junior : row.arm_number,
      registrationNumber: row.dog.registration_number || "",
      breed: row.dog.breed,
      ownerName: row.owner.display_name,
      ownerEmail: row.owner.email,
      handlerName: handlerName,
      // Registrants table requirement: show the registered user account email.
      handlerEmail: row.owner_account_email || row.owner?.email || "",
      show: row.show_type + " " + (row.custom_show_name || row.show_name),
      showDate: moment(row.show_date).format("MM/DD/YY"),
      registeredAt: moment(row.created_at).format("MM/DD/YY hh:mm:ssA"),
      actions: {
        registration: row,
        event: props.event
      }
    });
  }

  const getRegistrants = useCallback((id: string) => {
    let query:any = {
      sort: [{
        attribute_name: "show_type",
        sort: "asc"
      }, {
        attribute_name: "created_at",
        sort: "asc"
      }],
    };

    apiService.getEventShowRegistrations(id, query).then((response) => {
      let rows:IRow[] = [];

      for (let i in response) {
        let row:any = response[i];

        // Junior Showmanship-only entries carry a junior number and no regular one: a single row with the junior number
        if (row.arm_number_junior && !row.arm_number) {
          addRegistrantRow(row, rows, true);
          continue;
        }

        // add extra row for jr class to emulate separate record
        if (row.arm_number_junior && row.arm_number_junior < row.arm_number) {
          addRegistrantRow(row, rows, true);
        }

        addRegistrantRow(row, rows);

        // add extra row for jr class to emulate separate record
        if (row.arm_number_junior && row.arm_number_junior > row.arm_number) {
          addRegistrantRow(row, rows, true);
        }
      }

      setRowData(rows);
    });
  }, [props.event]);

  let colDefs:ColDef<IRow>[] = [
    { headerName: "Call Name (Reg. Name)", field: "callName", width: 260, minWidth: 160, maxWidth: 300,suppressSizeToFit: true, filter:true },
    { headerName: "Armband", field: "armband", width: 135, minWidth: 100, maxWidth: 150, suppressSizeToFit: true, cellDataType: "text", filter:true, comparator: (a: string, b: string) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return -1;
      if (isNaN(numB)) return 1;
      return numA - numB;
    }},
    { headerName: "Registration #", field: "registrationNumber", width: 160, minWidth: 100, maxWidth: 200, suppressSizeToFit: true, cellDataType: "text", filter:true },
    { headerName: "Breed", field: "breed", minWidth: 220, flex: 1, filter:true },
    { headerName: "Show Name", field: "show", width: 160, minWidth: 160, maxWidth: 180,suppressSizeToFit: true,filter:true },
    { headerName: "Show Date", field: "showDate", width: 140, minWidth: 140, maxWidth: 140,suppressSizeToFit: true, filter:true },
    { headerName: "Owner Name", field: "ownerName", minWidth: 180, flex: 1, cellDataType: "text", filter: "agTextColumnFilter" },
    { headerName: "Owner Email", field: "ownerEmail", minWidth: 180, flex: 1, cellDataType: "text", filter: "agTextColumnFilter" },
    { headerName: "Handler Name", field: "handlerName", minWidth: 180, flex: 1, cellDataType: "text", filter: "agTextColumnFilter" },
    { headerName: "Handler Email", field: "handlerEmail", minWidth: 200, flex: 1, cellDataType: "text", filter: "agTextColumnFilter" },
    { headerName: "Registered At", field: "registeredAt", width: 170, minWidth: 140, maxWidth: 170,suppressSizeToFit: true, filter:true },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        getRegistrants: () => { getRegistrants(props.event._id); }
      },
      resizable: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const [rowData, setRowData] = useState<IRow[]>([]);

  const defaultColDef: ColDef = {
    resizable: true
  };

  const emailRegistrants = async (payload: IEventMessage) => {
    setIsSending(true)
    
    // Close modal immediately for better UX
    toggle();
    
    try {
      // Get participant count for the success message
      const participantCountResult = await apiService.getEventParticipantCount(props.event._id);
      const participantCount = participantCountResult.count;

      const requests = [
        apiService.requestSendMessageEmailForEvent(props.event._id, payload),
      ];
      if (payload.add_armbands) {
        const currentUser = helperService.getUser();
        requests.push(
          apiService.generatedArmNumbersPerShowTypeEmails(
            props.event._id,
            payload.is_test_sending,
            payload.is_test_sending ? currentUser.email : undefined,
            payload.is_test_sending ? payload.test_target_user_id : undefined
          )
        );
      }

      const content = payload.is_test_sending 
        ? `Test email sent to ${props.event.club.email}.` 
        : `Your message has been sent to ${participantCount} current registrant${participantCount === 1 ? '' : 's'}.`;

      await Promise.all(requests);
      
      toast.show({
        title: "Email Results",
        content,
        duration: 10000,
        type: "success"
      });
    } catch (e: any) {
      toast.show({
        title: "Email Results",
        content: "Unable to email results",
        duration: 10000,
        errorDetails: e,
        type: "fail"
      });
    } finally {
      setIsSending(false);
    }
  }

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getRegistrants(props.event._id);
    }
  }, [props.currentTab, props.tabIndex, props.event._id, getRegistrants]);

  useEffect(() => {
    if (!loaded) {
      getRegistrants(props.event._id);
      setLoaded(true);
    }
  }, [loaded, getRegistrants, props.event._id]);

  useEffect(() => {
    let filters: IFilters = {};

    if (gridApi) {
      if (!!debouncedEmailSearch) {
        filters.ownerEmail = {
          filterType: "text",
          type: "contains",
          filter: debouncedEmailSearch,
        };
      } else {
        filters.ownerEmail = undefined;
      }

      if (!!debouncedOwnerNameSearch) {
        filters.ownerName = {
          filterType: "text",
          type: "contains",
          filter: debouncedOwnerNameSearch,
        };
      } else {
        filters.ownerName = undefined;
      }

      gridApi.setFilterModel(filters);
    }
  }, [gridApi, debouncedOwnerNameSearch, debouncedEmailSearch]);

  return (
    <div className={styles.RegistrantsTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="text"
            label="Owner Name"
            value={ownerNameSearch}
            onChange={(e) => { setOwnerNameSearch(e.target.value); }}
          />
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="email"
            label="Owner Email"
            value={emailSearch}
            onChange={(e) => { setEmailSearch(e.target.value); }}
          />
          <Button disabled={!(ownerNameSearch || emailSearch)} className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
        </div>
        <div className={styles.right}>
          <ContextMenu
            label="Actions"
            options={menuActions}
          />
        </div>
      </div>
      <div className={styles.tableWrap}>
      <AgGridReact
        suppressRowTransform
        pagination
        onGridReady={onGridReady}
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        theme={theme}
        paginationPageSize={20}
      />
      </div>
      <Modal
        isShown={isShown}
        hide={() => { toggle(); }}
        className={isShowingEmailConfirmation ? "emailConfirmationModal" : "sendMessageModal"}
        modalContent={
          <SendMessage
            isSending={isSending}
            handleSendMessage={emailRegistrants}
            subject={`New Message from Organizer for ${props.event.title}`}
            type={SendMessageType.Registrants}
            eventId={props.event._id}
            onGetParticipantCount={async () => {
              const result = await apiService.getEventParticipantCount(props.event._id);
              return result.count;
            }}
            onConfirmationStateChange={setIsShowingEmailConfirmation}
          />
        }
      />
      
      {/* Column Selection Modal for Registrations Export */}
      <Modal
        isShown={isColumnModalShown}
        hide={toggleColumnModal}
        className="columnSelectionModal"
        modalContent={
          <ColumnSelectionModal
            onConfirm={handleRegistrationsColumnSelection}
            onCancel={toggleColumnModal}
            columns={registrationColumns}
            exportName="Registrants"
          />
        }
      />

      <Modal
        isShown={isEntryFormsFilterShown}
        hide={toggleEntryFormsFilter}
        className="entryFormsFilterModal"
        modalContent={
          <EntryFormsFilterModal
            showTypes={props.event.show_types || []}
            onDownload={(selectedShowTypes) => {
              download.pushDownload({
                type: "print_registration",
                eventId: props.event._id,
                showTypes: selectedShowTypes,
              });
              toggleEntryFormsFilter();
            }}
            onCancel={toggleEntryFormsFilter}
          />
        }
      />

      <Modal
        isShown={isWeighInsShown}
        hide={toggleWeighIns}
        className="weighInsModal"
        modalContent={<WeighInsModal event={props.event} />}
      />
    </div>
  );
};

export default RegistrantsTab;
